import { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Form, FormGroup, Label, Input, FormFeedback, Spinner, Button } from 'reactstrap';
import { BiCheckCircle, BiErrorCircle } from 'react-icons/bi';
import debounce from 'lodash/debounce';
// Vite pre-bundles react-windowed-select's CJS default — normalise the
// default/namespace import the same way the other consumers do.
import WindowedSelectModule from 'react-windowed-select';
const WindowedSelect = WindowedSelectModule.default ?? WindowedSelectModule;
import useTheme from '../../hooks/useTheme';
import { customSelectTheme, selectStyle } from '../utility/selectStyling';
import { COUNTRIES } from '../../utils/countries';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currencies';
import { validateCustomer } from '../../utils/customerValidation';
import useUpdateCustomer from '../../hooks/useUpdateCustomer';

const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ value: c.code, label: c.name }));

// Fields this card edits. Kept as one list so seeding/diffing/serialising
// can't drift from what the UI actually shows.
const FIELDS = [
    'name', 'contactName', 'country', 'email', 'phone',
    'addressLine1', 'addressLine2', 'townCity', 'postcode',
    'currency',
];

// Normalise a fetched CustomerViewModel into a draft object — nullable
// strings become '' so inputs stay controlled. Currency is the exception:
// the select needs a valid code, so we fall back to the default.
function toDraft(customer) {
    const out = {};
    for (const f of FIELDS) out[f] = customer?.[f] ?? '';
    if (!out.currency) out.currency = DEFAULT_CURRENCY;
    return out;
}

function draftsEqual(a, b) {
    if (!a || !b) return a === b;
    return FIELDS.every(f => a[f] === b[f]);
}

export default function CustomerDetailsCard({ customer, dirtyRef, flushPendingRefresh }) {
    const theme = useTheme();
    const updateCustomer = useUpdateCustomer();

    const [draft, setDraft] = useState(() => toDraft(customer));
    // 'idle' — hidden pill; 'saving' — spinner; 'saved' — check (fades);
    // 'error' — red badge with retry. Derived from mutation state but
    // latched so the 'saved' check can linger after the mutation settles.
    const [saveState, setSaveState] = useState('idle');
    const savedFadeTimerRef = useRef(null);

    // Refs the debounced save reads from so we can create a single stable
    // debounce instance regardless of re-renders.
    const lastSavedRef = useRef(toDraft(customer));
    const seedIdRef = useRef(customer?.id ?? null);
    const draftRef = useRef(draft);
    // Mirror draft into a ref (written in an effect to keep render pure) so
    // the debounced save reads the latest value when it finally fires.
    useEffect(() => { draftRef.current = draft; });

    // Seed the draft once per customer id. After that, draft is user-owned
    // and we never blindly overwrite it from server data while dirty.
    useEffect(() => {
        if (customer?.id !== seedIdRef.current) {
            const fresh = toDraft(customer);
            setDraft(fresh);
            lastSavedRef.current = fresh;
            seedIdRef.current = customer?.id ?? null;
            if (dirtyRef) dirtyRef.current = false;
            setSaveState('idle');
        }
    }, [customer, dirtyRef]);

    // Reconcile from server when not dirty (e.g. an edit made in another tab
    // arrives via SignalR). Dirty edits stay on the user's screen untouched
    // — the useCustomer hook already skips invalidation while dirty, but
    // this belt-and-braces check handles races where data arrived just as
    // the user started typing.
    useEffect(() => {
        if (!dirtyRef?.current && customer) {
            const fresh = toDraft(customer);
            if (!draftsEqual(fresh, lastSavedRef.current)) {
                setDraft(fresh);
                lastSavedRef.current = fresh;
            }
        }
    }, [customer, dirtyRef]);

    const { errors, hasErrors } = validateCustomer(draft);
    const hasErrorsRef = useRef(hasErrors);
    useEffect(() => { hasErrorsRef.current = hasErrors; });

    // The save callback reads its inputs via refs so the debounced wrapper
    // can stay stable for the component's lifetime. Recreating the debounce
    // on every keystroke would defeat the point. The ref itself is mutated
    // inside an effect so render stays pure.
    const saveRef = useRef(null);
    useEffect(() => {
        saveRef.current = () => {
            if (hasErrorsRef.current) return; // validation gate — stay silent
            const id = seedIdRef.current;
            if (id == null) return;
            const body = draftRef.current;
            if (draftsEqual(body, lastSavedRef.current)) {
                // Nothing actually changed (e.g. user typed then undid).
                if (dirtyRef) dirtyRef.current = false;
                setSaveState('idle');
                return;
            }
            setSaveState('saving');
            updateCustomer.mutate({ id, body }, {
                onSuccess: (fresh) => {
                    lastSavedRef.current = toDraft(fresh);
                    // If the user typed more while the save was in-flight,
                    // fire again immediately. Otherwise, clear dirty and
                    // show 'saved'.
                    if (!draftsEqual(draftRef.current, lastSavedRef.current)) {
                        saveRef.current();
                    } else {
                        if (dirtyRef) dirtyRef.current = false;
                        setSaveState('saved');
                        flushPendingRefresh?.();
                        clearTimeout(savedFadeTimerRef.current);
                        savedFadeTimerRef.current = setTimeout(
                            () => setSaveState('idle'), 1500);
                    }
                },
                onError: () => {
                    setSaveState('error');
                },
            });
        };
    });

    const debouncedSave = useMemo(
        () => debounce(() => saveRef.current?.(), 600),
        []
    );

    // Flush any pending save if the component unmounts or the tab is
    // closing. The browser won't wait for the async POST to resolve, but
    // firing it at least gives the server a chance to persist.
    useEffect(() => {
        const onBeforeUnload = () => { debouncedSave.flush(); };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            debouncedSave.flush();
            clearTimeout(savedFadeTimerRef.current);
        };
    }, [debouncedSave]);

    const handleChange = (field, value) => {
        setDraft(prev => ({ ...prev, [field]: value }));
        if (dirtyRef) dirtyRef.current = true;
        // A typing storm after an error still produces a single save on
        // settle — lodash.debounce coalesces calls.
        debouncedSave();
    };

    return (
        // Use the shared .surface class (not reactstrap's Card) so the Details
        // panel visually matches the Quotes/Jobs DataTables on the same page —
        // same bg token, border, radius, and elevation.
        <div className="surface">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="fw-semibold">Details</span>
                <StatusPill state={saveState} onRetry={() => saveRef.current?.()} />
            </div>
            <Form noValidate>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Name *</Label>
                                <Input
                                    value={draft.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    invalid={!!errors.name}
                                />
                                <FormFeedback>{errors.name}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Contact Name *</Label>
                                <Input
                                    value={draft.contactName}
                                    onChange={(e) => handleChange('contactName', e.target.value)}
                                    invalid={!!errors.contactName}
                                />
                                <FormFeedback>{errors.contactName}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={8}>
                            <FormGroup>
                                <Label>Country *</Label>
                                <WindowedSelect
                                    options={COUNTRY_OPTIONS}
                                    value={COUNTRY_OPTIONS.find(o => o.value === draft.country) ?? null}
                                    onChange={(opt) => handleChange('country', opt?.value ?? '')}
                                    isClearable isSearchable
                                    styles={selectStyle}
                                    theme={customSelectTheme(theme)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    placeholder="Select a country..."
                                />
                                {errors.country && (
                                    <div className="invalid-feedback d-block">{errors.country}</div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Currency</Label>
                                <Input type="select" value={draft.currency}
                                       onChange={(e) => handleChange('currency', e.target.value)}>
                                    {CURRENCIES.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code} — {c.symbol} {c.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    value={draft.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    invalid={!!errors.email}
                                />
                                <FormFeedback>{errors.email}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Phone *</Label>
                                <Input
                                    value={draft.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    invalid={!!errors.phone}
                                />
                                <FormFeedback>{errors.phone}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    {errors.contactMethod && (
                        <div className="invalid-feedback d-block mb-3">{errors.contactMethod}</div>
                    )}
                    <FormGroup>
                        <Label>Address Line 1 *</Label>
                        <Input
                            value={draft.addressLine1}
                            onChange={(e) => handleChange('addressLine1', e.target.value)}
                            invalid={!!errors.addressLine1}
                        />
                        <FormFeedback>{errors.addressLine1}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>Address Line 2</Label>
                        <Input
                            value={draft.addressLine2}
                            onChange={(e) => handleChange('addressLine2', e.target.value)}
                        />
                    </FormGroup>
                    <Row>
                        <Col md={8}>
                            <FormGroup>
                                <Label>Town/City *</Label>
                                <Input
                                    value={draft.townCity}
                                    onChange={(e) => handleChange('townCity', e.target.value)}
                                    invalid={!!errors.townCity}
                                />
                                <FormFeedback>{errors.townCity}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Postcode *</Label>
                                <Input
                                    value={draft.postcode}
                                    onChange={(e) => handleChange('postcode', e.target.value)}
                                    invalid={!!errors.postcode}
                                />
                                <FormFeedback>{errors.postcode}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
        </div>
    );
}

function StatusPill({ state, onRetry }) {
    if (state === 'idle') return null;
    if (state === 'saving') {
        return (
            <span className="d-inline-flex align-items-center gap-1 text-muted small">
                <Spinner size="sm" /> Saving…
            </span>
        );
    }
    if (state === 'saved') {
        return (
            <span className="d-inline-flex align-items-center gap-1 text-success small">
                <BiCheckCircle /> Saved
            </span>
        );
    }
    // 'error'
    return (
        <span className="d-inline-flex align-items-center gap-1 text-danger small">
            <BiErrorCircle /> Save failed
            <Button size="sm" color="link" className="p-0 ms-1" onClick={onRetry}>
                retry
            </Button>
        </span>
    );
}
